/**
 * Écran « Trésorerie et besoin en fonds de roulement ».
 *
 * Un résultat n'est pas une trésorerie : c'est tout l'objet de cet écran. La
 * cascade en montre la raison exercice par exercice — amortissements, variation
 * du besoin en fonds de roulement, investissements.
 *
 * Aucun montant n'est calculé ici : tout vient du moteur.
 */

import { dateFr, entier, euros, eurosSigne, keuros, moisCourt, moisFr } from '@scc/core';
import type { Charge } from '../api.js';
import { h, vider } from '../dom.js';
import {
  TEINTES,
  graphiqueCascade,
  graphiqueEmpile,
  graphiqueLignes,
  legende,
  type EtapeCascade,
} from '../graphiques.js';
import { enteteSection, insight, kpi, noteLecture, regleAccent } from '../composants.js';

export function vueTresorerie(d: Charge): HTMLElement {
  const t = d.tresorerie;
  const parScenario = t.parScenario;
  const defaut = parScenario.find((x) => x.cle === 'sc3') ?? parScenario[0];

  // ── En-tête chiffré ────────────────────────────────────────────────────
  const dernierMois = t.mensuelle[t.mensuelle.length - 1];
  const soldeCourant = dernierMois?.solde ?? t.soldeOuverture;
  const premierPassage = defaut?.passage[0];
  const bfrCourant = defaut?.bfr[0];
  const creux = (defaut?.passage ?? []).reduce<{ montant: number; annee: number } | null>(
    (min, p) => (min === null || p.tresorerieCloture < min.montant ? { montant: p.tresorerieCloture, annee: p.exercice } : min),
    null,
  );

  const entete = h(
    'div',
    { classe: 'kpi-grid' },
    kpi('neutral', keuros(soldeCourant), null, `Trésorerie au ${dateFr(d.meta.dateArrete)}`, {
      sens: soldeCourant >= t.soldeOuverture ? 'up' : 'down',
      texte: `${eurosSigne(soldeCourant - t.soldeOuverture)} depuis le 1ᵉʳ janvier`,
    }),
    kpi(
      (premierPassage?.sousSeuil ?? false) ? 'warning' : 'progress',
      keuros(premierPassage?.tresorerieCloture ?? 0),
      null,
      `Trésorerie projetée fin ${premierPassage?.exercice ?? d.meta.exerciceCourant}`,
      {
        sens: (premierPassage?.sousSeuil ?? false) ? 'down' : 'up',
        texte: `seuil de sécurité ${keuros(premierPassage?.seuil ?? 0)}`,
      },
    ),
    kpi('neutral', keuros(bfrCourant?.bfr ?? 0), null, 'Besoin en fonds de roulement', {
      sens: 'neutre',
      texte: 'minoré — dettes fournisseurs absentes',
    }),
    kpi(
      creux !== null && creux.montant < (premierPassage?.plancher ?? 0) ? 'alert' : 'neutral',
      keuros(creux?.montant ?? 0),
      null,
      'Creux de trésorerie de la période',
      { sens: 'neutre', texte: `en ${creux?.annee ?? '—'}` },
    ),
  );

  // ── Trésorerie mensuelle constatée ─────────────────────────────────────
  const mensuelle = h(
    'div',
    {},
    graphiqueLignes(
      t.mensuelle.map((p) => moisCourt(p.mois)),
      [
        {
          nom: 'Solde bancaire',
          couleur: TEINTES.blue,
          valeurs: t.mensuelle.map((p) => p.solde),
        },
      ],
      {
        titre: `Trésorerie mensuelle constatée ${d.meta.exerciceCourant}`,
        enteteAbscisse: 'Mois',
        unite: 'euros',
        bande: t.mensuelle.map(() => premierPassage?.plancher ?? 0),
        nomBande: 'Plancher de trésorerie',
      },
    ),
    legende([
      { nom: 'Solde bancaire constaté', couleur: TEINTES.blue },
      { nom: 'Plancher de trésorerie', couleur: TEINTES.danger },
    ]),
  );

  const tableMensuelle = h(
    'table',
    { classe: 'comp-table encadre-tableau' },
    h('caption', { classe: 'invisible' }, 'Trésorerie mensuelle constatée'),
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { scope: 'col' }, 'Mois'),
        h('th', { scope: 'col' }, 'Encaissements'),
        h('th', { scope: 'col' }, 'Décaissements'),
        h('th', { scope: 'col' }, 'Variation'),
        h('th', { scope: 'col' }, 'Solde à la fin'),
      ),
    ),
    h(
      'tbody',
      {},
      ...t.mensuelle.map((p) =>
        h(
          'tr',
          {},
          h('th', { scope: 'row' }, moisFr(p.mois)),
          h('td', { classe: 'num' }, euros(p.encaissements)),
          h('td', { classe: 'num' }, euros(p.decaissements)),
          h('td', { classe: 'num' }, eurosSigne(p.variation)),
          h('td', { classe: 'num' }, euros(p.solde)),
        ),
      ),
    ),
  );

  // ── Sélecteur de scénario ──────────────────────────────────────────────
  const zoneScenario = h('div', {});
  let cleCourante = defaut?.cle ?? '';
  /** Exercice dont la cascade est affichée. */
  let exerciceCascade = defaut?.passage[0]?.exercice ?? d.meta.exerciceCourant;

  /** Somme des valeurs absolues : sert à savoir si la cascade a du relief. */
  const sommeAbsolue = (valeurs: readonly number[]): number =>
    valeurs.reduce((s, v) => s + Math.abs(v), 0);

  const dessinerScenario = (): void => {
    vider(zoneScenario);
    const sc = parScenario.find((x) => x.cle === cleCourante);
    if (!sc) return;

    const annees = sc.passage.map((p) => p.exercice);

    // Trajectoire annuelle de trésorerie, confrontée au seuil de sécurité.
    zoneScenario.appendChild(
      graphiqueLignes(
        annees,
        [
          {
            nom: 'Trésorerie de clôture',
            couleur: TEINTES.blue,
            valeurs: sc.passage.map((p) => p.tresorerieCloture),
          },
        ],
        {
          titre: `Trésorerie projetée — ${sc.libelle}`,
          enteteAbscisse: 'Exercice',
          unite: 'euros',
          bande: sc.passage.map((p) => p.seuil),
          nomBande: 'Seuil de sécurité',
          depuisZero: true,
        },
      ),
    );
    zoneScenario.appendChild(
      legende([
        { nom: 'Trésorerie de clôture', couleur: TEINTES.blue },
        { nom: 'Seuil de sécurité', couleur: TEINTES.danger },
      ]),
    );

    // Cascade d'un exercice, choisi : sur le premier, la variation du besoin en
    // fonds de roulement est nulle par construction — le ratio y est calibré —
    // et la cascade n'y montrerait donc que le résultat.
    const zoneCascade = h('div', {});
    const dessinerCascade = (): void => {
      vider(zoneCascade);
      const p = sc.passage.find((x) => x.exercice === exerciceCascade) ?? sc.passage[0];
      if (!p) return;
      const etapes: EtapeCascade[] = [
        { libelle: `Trésorerie fin ${p.exercice - 1}`, montant: p.tresorerieOuverture, type: 'borne' },
        { libelle: 'Résultat', montant: p.resultat, type: 'flux' },
        { libelle: 'Amortissements', montant: p.amortissements, type: 'flux' },
        { libelle: 'Variation du BFR', montant: -p.variationBfr, type: 'flux' },
        { libelle: 'Investissements', montant: -p.investissements, type: 'flux' },
        { libelle: `Trésorerie fin ${p.exercice}`, montant: p.tresorerieCloture, type: 'borne' },
      ];
      zoneCascade.appendChild(
        graphiqueCascade(etapes, {
          titre: `Du résultat à la trésorerie — exercice ${p.exercice}`,
          enteteAbscisse: 'Étape',
        }),
      );
      const ecart = sommeAbsolue([p.amortissements, p.variationBfr, p.investissements]);
      zoneCascade.appendChild(
        insight(
          'info',
          '≡',
          ecart === 0
            ? `**Lecture.** Sur cet exercice, la trésorerie varie exactement comme le résultat : ` +
                `${eurosSigne(p.resultat)}. Ni amortissement, ni investissement, et un besoin en fonds de ` +
                `roulement inchangé — les produits ne bougent pas d’un exercice à l’autre.`
            : `**Lecture.** Un résultat de ${eurosSigne(p.resultat)} ne fait pas varier la trésorerie du même ` +
                `montant : les amortissements (${euros(p.amortissements)}) ne se décaissent pas, la variation du ` +
                `besoin en fonds de roulement mobilise ${eurosSigne(-p.variationBfr)}, et les investissements ` +
                `${euros(p.investissements)}. La trésorerie varie au total de ${eurosSigne(p.variationTresorerie)}.`,
        ),
      );
    };

    zoneScenario.appendChild(
      h(
        'div',
        { classe: 'barre-actions' },
        h('span', { classe: 'titre' }, 'Cascade de l’exercice'),
        h(
          'select',
          {
            'aria-label': 'Exercice de la cascade résultat vers trésorerie',
            surChange: (ev) => {
              exerciceCascade = Number((ev.currentTarget as HTMLSelectElement).value);
              dessinerCascade();
            },
          },
          ...sc.passage.map((p) =>
            h(
              'option',
              { value: String(p.exercice), ...(p.exercice === exerciceCascade ? { selected: true } : {}) },
              String(p.exercice),
            ),
          ),
        ),
      ),
    );
    zoneScenario.appendChild(zoneCascade);
    dessinerCascade();

    // Besoin en fonds de roulement : la position nette en barres, sa variation
    // en courbe — c'est la variation, non le niveau, qui consomme du cash.
    zoneScenario.appendChild(
      graphiqueEmpile(
        sc.bfr.map((b) => b.exercice),
        [{ nom: 'BFR net', couleur: TEINTES.green, valeurs: sc.bfr.map((b) => b.bfr) }],
        {
          titre: `Besoin en fonds de roulement — ${sc.libelle}`,
          enteteAbscisse: 'Exercice',
          ligne: {
            nom: 'Variation du BFR',
            couleur: TEINTES.rose,
            valeurs: sc.bfr.map((b) => b.variation),
          },
        },
      ),
    );
    zoneScenario.appendChild(
      legende([
        { nom: 'BFR net à la clôture', couleur: TEINTES.green },
        { nom: 'Variation sur l’exercice', couleur: TEINTES.rose },
      ]),
    );

    // Tableau du passage, exercice par exercice.
    zoneScenario.appendChild(
      h(
        'table',
        { classe: 'comp-table encadre-tableau' },
        h('caption', { classe: 'invisible' }, `Passage du résultat à la trésorerie — ${sc.libelle}`),
        h(
          'thead',
          {},
          h(
            'tr',
            {},
            h('th', { scope: 'col' }, 'Exercice'),
            h('th', { scope: 'col' }, 'Ouverture'),
            h('th', { scope: 'col' }, 'Résultat'),
            h('th', { scope: 'col' }, 'Amortissements'),
            h('th', { scope: 'col' }, 'Δ BFR'),
            h('th', { scope: 'col' }, 'Investissements'),
            h('th', { scope: 'col' }, 'Δ trésorerie'),
            h('th', { scope: 'col' }, 'Clôture'),
            h('th', { scope: 'col' }, 'Seuil'),
          ),
        ),
        h(
          'tbody',
          {},
          ...sc.passage.map((p) =>
            h(
              'tr',
              { classe: p.sousSeuil ? 'ligne-alerte' : '' },
              h('th', { scope: 'row' }, String(p.exercice)),
              h('td', { classe: 'num' }, euros(p.tresorerieOuverture)),
              h('td', { classe: 'num' }, eurosSigne(p.resultat)),
              h('td', { classe: 'num' }, euros(p.amortissements)),
              h('td', { classe: 'num' }, eurosSigne(-p.variationBfr)),
              h('td', { classe: 'num' }, euros(p.investissements)),
              h('td', { classe: 'num' }, eurosSigne(p.variationTresorerie)),
              h('td', { classe: 'num' }, euros(p.tresorerieCloture)),
              h('td', { classe: 'num' }, euros(p.seuil)),
            ),
          ),
        ),
      ),
    );
  };

  const selecteur = h(
    'div',
    { classe: 'barre-actions' },
    h('span', { classe: 'titre' }, 'Scénario'),
    h(
      'select',
      {
        'aria-label': 'Scénario de projection de trésorerie',
        surChange: (ev) => {
          cleCourante = (ev.currentTarget as HTMLSelectElement).value;
          dessinerScenario();
        },
      },
      ...parScenario.map((x) =>
        h('option', { value: x.cle, ...(x.cle === cleCourante ? { selected: true } : {}) }, x.libelle),
      ),
    ),
  );

  dessinerScenario();

  return h(
    'div',
    { classe: 'page', id: 'page-tresorerie', role: 'region', 'aria-label': 'Trésorerie et besoin en fonds de roulement' },
    h(
      'section',
      {},
      enteteSection(
        1,
        'Trésorerie et besoin en fonds de roulement',
        'Ce que le résultat ne dit pas',
      ),
      entete,
      insight('warning', '⚠', `**Portée du calcul.** ${t.mentionMinoration}`),
      regleAccent(`Trésorerie constatée ${d.meta.exerciceCourant}`),
      mensuelle,
      tableMensuelle,
      noteLecture(
        `Les soldes mensuels sont reconstitués des mouvements du compte d'exploitation, à partir de ` +
          `${euros(t.soldeOuverture)} au 1ᵉʳ janvier. Ils s'arrêtent à la date d'arrêté : une trésorerie mensuelle ` +
          `est un constat, pas une projection.`,
      ),
      regleAccent('Projection pluriannuelle'),
      selecteur,
      zoneScenario,
      regleAccent('Composition du besoin en fonds de roulement'),
      h(
        'table',
        { classe: 'comp-table encadre-tableau' },
        h('caption', { classe: 'invisible' }, 'Composantes constatées du besoin en fonds de roulement'),
        h(
          'thead',
          {},
          h(
            'tr',
            {},
            h('th', { scope: 'col' }, 'Composante'),
            h('th', { scope: 'col' }, `Constaté au ${dateFr(t.decomposition.dateArrete)}`),
            h('th', { scope: 'col' }, 'Assiette'),
          ),
        ),
        h(
          'tbody',
          {},
          h(
            'tr',
            {},
            h('th', { scope: 'row' }, 'Créances de cotisation'),
            h('td', { classe: 'num' }, euros(t.decomposition.creancesCotisations)),
            h(
              'td',
              {},
              `${euros(t.decomposition.cotisationsAppelees)} appelés sur ${entier(d.adherents.appelsExerciceCourant)} appels émis`,
            ),
          ),
          h(
            'tr',
            {},
            h('th', { scope: 'row' }, 'Subventions et conventions à recevoir'),
            h('td', { classe: 'num' }, euros(t.decomposition.creancesSubventions)),
            h(
              'td',
              {},
              d.projection.versementsNotifies.length > 0
                ? `${euros(d.projection.conventionnelProjete)} attendus, dont des versements notifiés (${euros(d.previsionnelVote.totalConventionnel)} votés)`
                : `${euros(d.previsionnelVote.totalConventionnel)} votés`,
            ),
          ),
          h(
            'tr',
            {},
            h('th', { scope: 'row' }, 'Dettes sociales'),
            h('td', { classe: 'num' }, euros(t.decomposition.dettesSociales)),
            h('td', {}, `${euros(d.paie?.exerciceCourant.masseChargee ?? 0)} de masse chargée`),
          ),
          h(
            'tr',
            {},
            h('th', { scope: 'row' }, 'Position nette à cette date'),
            h('td', { classe: 'num' }, euros(t.decomposition.net)),
            h('td', {}, 'créances moins dettes sociales'),
          ),
          h(
            'tr',
            {},
            h('th', { scope: 'row' }, `BFR mesuré au 31 décembre ${d.meta.exerciceCourant - 1}`),
            h('td', { classe: 'num' }, euros(t.bfrOuverture)),
            h(
              'td',
              {},
              `${euros(d.chainage.fondsPropresCloture)} de fonds propres moins ${euros(t.soldeOuverture)} de disponibilités`,
            ),
          ),
        ),
      ),
      noteLecture(
        `**Deux mesures distinctes, à ne pas confondre.** Le tableau du haut est un constat de milieu ` +
          `d'exercice : au ${dateFr(t.decomposition.dateArrete)}, les cotisations ne sont pas toutes appelées et ` +
          `les subventions pas toutes versées. Il renseigne sur ce qui reste à encaisser, non sur un besoin ` +
          `structurel. La projection, elle, part de la **position mesurée au 31 décembre** — fonds propres moins ` +
          `disponibilités, soit ${euros(t.bfrOuverture)}, ou ${(t.ratio * 100).toFixed(1).replace('.', ',')} % des ` +
          `produits — et l'applique aux produits de chaque exercice du scénario. ${t.mentionOuverture}`,
      ),
    ),
  );
}
